import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box, Typography, IconButton, Button, TextField, CircularProgress,
    Alert, Card, CardContent, Chip, Divider,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon, QrCodeScanner as ScanIcon,
    Stop as StopIcon, Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import axiosInstance from '../../../axiosInstance';
import extEquipmentService from '../../../services/extEquipmentService';
import { extractQrToken } from '../../../utils/qrToken';
import { FORM_TYPES } from './constants';

export default function ExtEquipmentScan() {
    const navigate = useNavigate();
    const [scanning, setScanning] = useState(false);
    const [manualToken, setManualToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const scannerRef = useRef(null);
    const scannerDivId = 'qr-scanner-region';

    const stopScanner = useCallback(async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
                scannerRef.current.clear();
            } catch (e) { /* ignore */ }
            scannerRef.current = null;
        }
        setScanning(false);
    }, []);

    useEffect(() => {
        return () => { stopScanner(); };
    }, [stopScanner]);

    const handleQrResult = useCallback(async (qrToken) => {
        await stopScanner();
        await checkToken(qrToken);
    }, [stopScanner]);

    const startScanner = async () => {
        setError(null);
        setScanning(true);

        try {
            // Explicitly request camera permission first
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setError('Camera is not available on this device. Use manual input.');
                setScanning(false);
                return;
            }

            // Request permission — this triggers the browser prompt if not yet decided
            const tempStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            // Release the stream immediately — Html5Qrcode will open its own
            tempStream.getTracks().forEach(track => track.stop());

            const scanner = new Html5Qrcode(scannerDivId);
            scannerRef.current = scanner;

            await scanner.start(
                { facingMode: 'environment' },
                { fps: 30, qrbox: { width: 280, height: 280 }, aspectRatio: 1.0, disableFlip: false },
                (decodedText) => {
                    const token = extractQrToken(decodedText);
                    if (token) {
                        handleQrResult(token);
                    } else {
                        setError('Invalid QR code format');
                    }
                },
                () => {} // ignore errors during scanning
            );
        } catch (e) {
            console.error('Scanner error:', e);
            if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
                setError('Camera access denied. Please enable camera permission in your browser settings for this site and try again.');
            } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
                setError('No camera found on this device. Use manual input.');
            } else if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
                setError('Camera is in use by another app. Close it and try again.');
            } else {
                setError('Failed to access camera. Use manual input.');
            }
            setScanning(false);
        }
    };

    const checkToken = async (token) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/ext-equipment/scan/${token}`);
            const data = res.data;

            if (data.type === 'internal' && data.existingTagToken) {
                // Internal with existing tag — check if linked
                if (data.linked) {
                    navigate(`/operations/ext-equipment/profile?qrToken=${data.existingTagToken}`);
                } else {
                    navigate(`/operations/ext-equipment/link?qrToken=${token}&type=internal&equipmentId=${data.equipmentId}`);
                }
            } else if (data.type === 'internal' && !data.existingTagToken) {
                // Internal, no tag yet — go to link form to create
                navigate(`/operations/ext-equipment/link?qrToken=${token}&type=internal&equipmentId=${data.equipmentId}`);
            } else if (data.type === 'external') {
                if (data.linked) {
                    navigate(`/operations/ext-equipment/profile?qrToken=${token}`);
                } else {
                    navigate(`/operations/ext-equipment/link?qrToken=${token}&type=external`);
                }
            }
        } catch (e) {
            if (e.response?.status === 404) {
                setError('This QR code is not registered. Only tags generated from the admin panel can be used.');
            } else {
                setError('Failed to check QR code. Check your connection.');
            }
            setLoading(false);
        }
    };

    const handleManualSubmit = () => {
        const token = extractQrToken(manualToken);
        if (!token) {
            setError('Enter a valid QR token (32 hex characters) or scan URL');
            return;
        }
        checkToken(token);
    };

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/main')} edge="start"><ArrowBackIcon /></IconButton>
                    <Typography variant="h6" fontWeight={600}>Equipment Inspection</Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 500, mx: 'auto' }}>

                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                    {loading && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <CircularProgress size={48} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Checking QR code...</Typography>
                        </Box>
                    )}

                    {!loading && (
                        <>
                            {/* Scanner */}
                            <Card sx={{ mb: 2 }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                                        Scan Equipment QR Tag
                                    </Typography>

                                    {!scanning ? (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            startIcon={<ScanIcon />}
                                            onClick={startScanner}
                                            sx={{ py: 3, fontSize: '1.1rem', fontWeight: 700 }}
                                        >
                                            Open Camera & Scan
                                        </Button>
                                    ) : (
                                        <>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 1,
                                                mb: 1.5,
                                                py: 1,
                                                px: 2,
                                                backgroundColor: '#e3f2fd',
                                                borderRadius: 1,
                                            }}>
                                                <ScanIcon sx={{ color: '#1565c0', fontSize: 20, animation: 'pulse 1.5s infinite' }} />
                                                <Typography variant="body2" fontWeight={600} color="#1565c0">
                                                    Point camera at the QR code on the equipment tag
                                                </Typography>
                                            </Box>
                                            <Box
                                                id={scannerDivId}
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    mx: 'auto',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    mb: 1.5,
                                                    border: '3px solid #1976d2',
                                                    position: 'relative',
                                                    '& video': { borderRadius: 1 },
                                                    '@keyframes pulse': {
                                                        '0%, 100%': { opacity: 1 },
                                                        '50%': { opacity: 0.4 },
                                                    },
                                                    '@keyframes scanline': {
                                                        '0%': { top: '10%' },
                                                        '50%': { top: '85%' },
                                                        '100%': { top: '10%' },
                                                    },
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        left: '15%',
                                                        right: '15%',
                                                        height: '2px',
                                                        backgroundColor: '#4caf50',
                                                        boxShadow: '0 0 8px 2px rgba(76,175,80,0.5)',
                                                        animation: 'scanline 2.5s ease-in-out infinite',
                                                        zIndex: 10,
                                                        pointerEvents: 'none',
                                                    },
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                startIcon={<StopIcon />}
                                                onClick={stopScanner}
                                                fullWidth
                                            >
                                                Stop Scanner
                                            </Button>
                                        </>
                                    )}
                                </CardContent>
                            </Card>

                            <Divider sx={{ my: 2 }}>
                                <Chip label="OR" size="small" />
                            </Divider>

                            {/* Manual input */}
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                        Enter Token Manually
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Paste token or scan URL..."
                                            value={manualToken}
                                            onChange={e => setManualToken(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleManualSubmit(); }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleManualSubmit}
                                            disabled={!manualToken.trim()}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            <SearchIcon />
                                        </Button>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Token is 32 characters from the printed QR label, or paste the full URL
                                    </Typography>
                                </CardContent>
                            </Card>
                        </>
                    )}

                </Box>
            </Box>
        </Box>
    );
}
